import React, { useEffect, useState, useMemo } from 'react';
import { fetchCostCategories, fetchNewCostQuantityTypes, addNewCo2Weight, fetchRegions, saveAthenaMurOc23 } from '../services/api';
import { useRegion } from '../context/RegionContext';
import FlowPickerModal from './FlowPickerModal';
import { auth } from '../firebaseConfig';
import { v4 as uuidv4 } from 'uuid';
import './PageLayout.css';

function NewCo2WeightPage() {
    const { selectedRegion } = useRegion();
    
    const [categories, setCategories] = useState([]);
    const [quantityTypes, setQuantityTypes] = useState([]);
    const [availableRegions, setAvailableRegions] = useState([]);
    const [showPopup, setShowPopup] = useState(false);
    
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingQuantityTypes, setLoadingQuantityTypes] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [form, setForm] = useState({
        Id: '',
        Name: '',
        Category: '',
        QuantityType: '',
        Co2Value: '',
        BiogenicValue: '',
        WeightValue: '',
        ExternalRefType: '',
        ExternalRefId: ''
    });

    // Athena Flow lock (USA-Canada only). Holds the picked flow until submit, then written to
    // Athena_mur_oc23_map keyed to this product's base MUR id (+ 'vquest-Co2' on the backend).
    const [athenaFlow, setAthenaFlow] = useState(null);
    const isAthenaRegion = selectedRegion === 'vquest-us-1';

    useEffect(() => {
        (async () => {
            try {
                const regions = await fetchRegions();
                console.log('🔍 NEW CO2 WEIGHT: Fetched regions:', regions);
                setAvailableRegions(Array.isArray(regions) ? regions : []);
            } catch (e) {
                console.error('Error loading regions:', e);
                setAvailableRegions([]);
            }
        })();
    }, []);

    const [manualIdMode, setManualIdMode] = useState(false); // New state for toggle

    const regionName = useMemo(() => {
        if (!Array.isArray(availableRegions) || availableRegions.length === 0) {
            return selectedRegion;
        }
        const r = availableRegions.find((r) => r.id === selectedRegion);
        return r ? r.name : selectedRegion;
    }, [availableRegions, selectedRegion]);

    // ✅ NEW: Filter categories to only show EPD categories
    const epdCategories = useMemo(() => {
        if (!Array.isArray(categories)) {
            return [];
        }
        
        const filtered = categories.filter(cat => 
            cat.Categories && cat.Categories.trim().toUpperCase().startsWith('EPD')
        );
        
        console.log('🔍 Filtering categories:', {
            total: categories.length,
            epdOnly: filtered.length,
            sample: filtered.slice(0, 3).map(c => c.Categories)
        });
        
        return filtered;
    }, [categories]);

    useEffect(() => {
        if (!selectedRegion) {
            console.log('🔍 NEW CO2 WEIGHT: No selectedRegion yet');
            return;
        }

        const loadCategories = async () => {
            setLoadingCategories(true);
            try {
                console.log('🔍 NEW CO2 WEIGHT: Loading categories for region:', selectedRegion);
                const categoriesData = await fetchCostCategories(selectedRegion);
                console.log("📌 Categories for region", selectedRegion, ":", categoriesData);
                
                if (Array.isArray(categoriesData)) {
                    setCategories(categoriesData);
                } else if (categoriesData && Array.isArray(categoriesData.data)) {
                    setCategories(categoriesData.data);
                } else {
                    console.warn('Categories data is not an array:', categoriesData);
                    setCategories([]);
                }
            } catch (error) {
                console.error('❌ Error fetching categories:', error);
                setCategories([]);
            } finally {
                setLoadingCategories(false);
            }
        };

        const loadQuantityTypes = async () => {
            setLoadingQuantityTypes(true);
            try {
                console.log('🔍 NEW CO2 WEIGHT: Loading quantity types for region:', selectedRegion);
                const quantityTypesData = await fetchNewCostQuantityTypes(selectedRegion);
                console.log("📌 Quantity Types for region", selectedRegion, ":", quantityTypesData);
                
                if (Array.isArray(quantityTypesData)) {
                    setQuantityTypes(quantityTypesData);
                } else if (quantityTypesData && Array.isArray(quantityTypesData.data)) {
                    setQuantityTypes(quantityTypesData.data);
                } else {
                    console.warn('Quantity types data is not an array:', quantityTypesData);
                    setQuantityTypes([]);
                }
            } catch (error) {
                console.error('❌ Error fetching quantity types:', error);
                setQuantityTypes([]);
            } finally {
                setLoadingQuantityTypes(false);
            }
        };

        loadCategories();
        loadQuantityTypes();
    }, [selectedRegion]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
    };

    // Generate new UUID
    const generateGUID = () => {
        const newId = uuidv4();
        setForm((prevForm) => ({ ...prevForm, Id: newId }));
        console.log('🆔 Generated new ID:', newId);
    };

    // Generate initial ID on component mount (only in auto mode)
    useEffect(() => {
        if (!manualIdMode && !form.Id) {
            generateGUID();
        }
    }, [manualIdMode]); // Re-run if mode changes



    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (submitting) return;
        setSubmitting(true);

        // ✅ Ensure ID is set (generate if empty)
        let finalId = form.Id;
        if (!finalId || finalId.trim() === '') {
            finalId = uuidv4();
            setForm((prev) => ({ ...prev, Id: finalId }));
            console.log('🆔 Auto-generated ID on submit:', finalId);
        }

        const formattedData = {
            id: finalId,  // ✅ Use the confirmed ID
            name: form.Name,
            categoryId: form.Category,
            quantityTypeId: String(form.QuantityType),
            co2Value: parseFloat(form.Co2Value) || 0,
            biogenicValue: parseFloat(form.BiogenicValue) || 0,
            weightValue: parseFloat(form.WeightValue) || 0,
            externalRefType: form.ExternalRefType || null,  
            externalRefId: form.ExternalRefId || null
        };

        console.log("🔹 Submitting CO2 data with ID:", finalId);
        console.log("🔹 Full payload:", formattedData);

        try {
            const response = await addNewCo2Weight(formattedData, selectedRegion);

            console.log('✅ Success! Response:', response);
            console.log('✅ Submitted with ID:', finalId);

            // Athena flow lock — USA-Canada only, and only when a flow was picked. Separate
            // additive write; the endpoint appends 'vquest-Co2' to match the created MUR + view.
            if (isAthenaRegion && athenaFlow) {
                try {
                    await saveAthenaMurOc23({
                        murId: finalId,
                        flow: athenaFlow.flow,
                        flowTypeOc23: athenaFlow.flowTypeOc23,
                        unit: athenaFlow.unit,
                        flowGroup: athenaFlow.flowGroup,
                    });
                    setAthenaFlow(null);
                } catch (err) {
                    console.error('⚠️ Product saved but Athena flow mapping failed:', err);
                }
            }

            setShowPopup(true);

            // ✅ Reset form - generate new ID only if in auto mode
            if (manualIdMode) {
                setForm({
                    Id: '',  // Clear for next manual entry
                    Name: '',
                    Category: '',
                    QuantityType: '',
                    Co2Value: '',
                    BiogenicValue: '',
                    WeightValue: '',
                    ExternalRefType: '',   
                    ExternalRefId: ''
                });
            } else {
                const newId = uuidv4();
                setForm({
                    Id: newId,  // Generate new ID for next entry
                    Name: '',
                    Category: '',
                    QuantityType: '',
                    Co2Value: '',
                    BiogenicValue: '',
                    WeightValue: '',
                    ExternalRefType: '',   
                    ExternalRefId: ''
                });
                console.log('🆔 Generated new ID for next entry:', newId);
            }

        } catch (error) {
            console.error("❌ Error saving data:", error.response?.data || error);
            
            const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to add data';
            alert(`Error: ${errorMessage}`);
            
        } finally {
            setSubmitting(false);
        }
    };


    console.log('🔍 NEW CO2 WEIGHT RENDER STATE:', {
        selectedRegion,
        categoriesLength: categories?.length || 0,
        epdCategoriesLength: epdCategories?.length || 0,
        quantityTypesLength: quantityTypes?.length || 0,
        categoriesIsArray: Array.isArray(categories),
        quantityTypesIsArray: Array.isArray(quantityTypes)
    });

    return (
        <div className="rate-builder-page">
            <div className="page-title-section">
                <h1>New CO2, Biogenic, and Weight Data</h1>
                <div className="region-indicator-under-title">
                    🌏 Current Region: <strong>{regionName}</strong>
                </div>
            </div>

            {showPopup && (
                <div className="pop-up">
                    <div className="pop-up-content">
                        <h2>✅ Success</h2>
                        <p>New CO2/Biogenic/Weight Data Added to {regionName}!</p>
                        <button onClick={() => setShowPopup(false)}>OK</button>
                    </div>
                </div>
            )}

            <div className="form-container">
                <form onSubmit={handleSubmit}>
                    <div className={`input-group id-section-container ${submitting ? 'disabled' : ''}`}>
                    <div className="id-header-row">
                        <label className="id-section-label">ID</label>
                        <span className={`id-mode-pill ${manualIdMode ? 'manual' : 'auto'}`}>
                        {manualIdMode ? 'Manual' : 'Auto'}
                        </span>
                    </div>

                    <div className="id-input-wrapper">
                        {/* ID Input */}
                        <div className="id-input-container">
                        <input
                            type="text"
                            name="Id"
                            value={form.Id}
                            onChange={manualIdMode ? handleInputChange : undefined}
                            readOnly={!manualIdMode}
                            placeholder={
                            manualIdMode
                                ? "e.g., CUSTOM_CO2_2025_001"
                                : "Auto-generated UUID"
                            }
                            className={`id-input-field ${manualIdMode ? 'manual-mode' : 'auto-mode'}`}
                            disabled={submitting}
                        />

                        {!manualIdMode && (
                            <span className="id-lock-icon">🔒</span>
                        )}
                        </div>
                                                <label className="id-manual-toggle">
                            <input
                            type="checkbox"
                            className="id-manual-checkbox"
                            checked={manualIdMode}
                            onChange={(e) => {
                                const isManual = e.target.checked;
                                setManualIdMode(isManual);

                                if (!isManual) {
                                // switching back to auto
                                generateGUID();
                                } else {
                                // switching to manual
                                setForm((prev) => ({ ...prev, Id: '' }));
                                }
                            }}
                            disabled={submitting}
                            />
                            <span>Manual</span>
                        </label>

                        {/* Right-hand controls: Regenerate + Manual toggle */}
                        <div className="id-side-controls">
                        <button
                            type="button"
                            className="id-regenerate-btn"
                            onClick={generateGUID}
                            disabled={submitting || manualIdMode}
                            title={manualIdMode ? "Switch to Auto to regenerate" : "Generate a new ID"}
                        >
                            <span>🔄</span>
                            <span>Regenerate</span>
                        </button>


                        </div>
                    </div>
                    </div>        

                    <div className="input-group">
                        <label>Name</label>
                        <input 
                            type="text" 
                            name="Name" 
                            value={form.Name} 
                            onChange={handleInputChange} 
                            required 
                            disabled={submitting}
                        />
                    </div>

                    <div className="input-group">
                        <label>Category (EPD Only)</label>
                        <select 
                            name="Category" 
                            value={form.Category} 
                            onChange={handleInputChange} 
                            required
                            disabled={submitting || loadingCategories}
                        >
                            <option value="">Select EPD Category</option>
                            {loadingCategories ? (
                                <option disabled>Loading categories...</option>
                            ) : (
                                epdCategories.length > 0 ? (
                                    epdCategories.map((cat) => (
                                        <option key={cat.Id} value={cat.Id}>{cat.Categories}</option>
                                    ))
                                ) : (
                                    <option disabled>No EPD categories available</option>
                                )
                            )}
                        </select>
                        {!loadingCategories && epdCategories.length === 0 && categories.length > 0 && (
                            <small style={{ color: '#e74c3c', marginTop: '5px', display: 'block' }}>
                                ⚠️ No EPD categories found. Showing categories starting with "EPD" only.
                            </small>
                        )}
                    </div>

                    <div className="input-group">
                        <label>Quantity Type</label>
                        <select 
                            name="QuantityType" 
                            value={form.QuantityType} 
                            onChange={handleInputChange} 
                            required
                            disabled={submitting || loadingQuantityTypes}
                        >
                            <option value="">Select Quantity Type</option>
                            {loadingQuantityTypes ? (
                                <option disabled>Loading quantity types...</option>
                            ) : (
                                Array.isArray(quantityTypes) && quantityTypes.length > 0 ? (
                                    quantityTypes.map((qt) => (
                                        <option key={qt.Id} value={qt.Id}>{qt.Name}</option>
                                    ))
                                ) : (
                                    <option disabled>No quantity types available</option>
                                )
                            )}
                        </select>
                    </div>

                    <div className="input-group">
                        <label>CO2 Value</label>
                        <input 
                            type="number" 
                            step="any" 
                            name="Co2Value" 
                            value={form.Co2Value} 
                            onChange={handleInputChange} 
                            required 
                            disabled={submitting}
                        />
                    </div>

                    <div className="input-group">
                        <label>Biogenic Value</label>
                        <input 
                            type="number" 
                            step="any" 
                            name="BiogenicValue" 
                            value={form.BiogenicValue} 
                            onChange={handleInputChange} 
                            required 
                            disabled={submitting}
                        />
                    </div>

                    <div className="input-group">
                        <label>Weight Value</label>
                        <input 
                            type="number" 
                            step="any" 
                            name="WeightValue" 
                            value={form.WeightValue} 
                            onChange={handleInputChange} 
                            required 
                            disabled={submitting}
                        />
                    </div>

                    <div className="input-group">
                        <label>External Ref Type</label>
                        <input 
                            type="text" 
                            name="ExternalRefType" 
                            value={form.ExternalRefType} 
                            onChange={handleInputChange}
                            placeholder="e.g., NABERS, BRANZ, etc."
                            maxLength="45"
                            disabled={submitting}
                        />
                        <small style={{ color: '#7f8c8d', fontSize: '12px' }}>Optional - Reference type identifier</small>
                    </div>

                    <div className="input-group">
                        <label>External Ref ID</label>
                        <input 
                            type="text" 
                            name="ExternalRefId" 
                            value={form.ExternalRefId} 
                            onChange={handleInputChange}
                            placeholder="e.g., 01S45-SD5"
                            maxLength="255"
                            disabled={submitting}
                        />
                        <small style={{ color: '#7f8c8d', fontSize: '12px' }}>Optional - External reference identifier</small>
                    </div>

                    {/* Athena Flow (USA-Canada only): locks this product to an Athena flow + its
                        OC23 flow type. On export, the flow name replaces the nezo product name. */}
                    {isAthenaRegion && (
                        <div className="input-group">
                            <label>
                                Athena Flow{' '}
                                <span style={{
                                    fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: '#2f7d72',
                                    background: '#eef5f3', padding: '2px 6px', borderRadius: 4, marginLeft: 6,
                                }}>USA-CANADA</span>
                            </label>
                            <FlowPickerModal
                                value={athenaFlow?.value || ''}
                                selectedLabel={athenaFlow?.label || ''}
                                onChange={(opt) => setAthenaFlow(opt)}
                                placeholder="Select an Athena flow…"
                                title="Select Athena Flow"
                                disabled={submitting}
                            />
                            {athenaFlow && (
                                <small style={{ color: '#2f7d72', fontSize: 12 }}>
                                    🔒 {athenaFlow.flow} → {athenaFlow.flowTypeOc23}
                                    {athenaFlow.unit ? ` · ${athenaFlow.unit}` : ''}
                                </small>
                            )}
                        </div>
                    )}

                    <button type="submit" disabled={submitting}>
                        {submitting ? 'Adding Data...' : 'Add Data'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default NewCo2WeightPage;
